import { Illustration, Text, type UseModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { SupportContext } from "@src/appV2/support/constants";
import { type ReactElement } from "react";
import { useHistory } from "react-router-dom";

import { TryDifferentAuthMethodSection } from "../components/TryDifferentAuthMethodSection";
import { WorkplaceAppDownloadCard } from "../components/WorkplaceAppDownloadCard";
import { AuthMode } from "../libs";
import { DialogAuthMethod, type DialogAuthMethodType } from "../types";

interface Props {
  modalState: UseModalState;
  authMethod: DialogAuthMethodType;
}

export function SignUpInsteadDialog(props: Props): ReactElement {
  const { modalState, authMethod } = props;
  const history = useHistory();

  const theme = getTheme();

  return (
    <ThemeProvider theme={theme}>
      <FullScreenDialog modalState={modalState} variant="tertiary">
        <PageHeaderTopRow
          onClickBack={() => {
            modalState.closeModal();
            logEvent(APP_V2_USER_EVENTS.CLOSE_PHONE_NUMBER_EXISTS_DIALOG);
          }}
        />

        <Box
          sx={{
            flex: 1,
            padding: theme.spacing(6),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            spacing={8}
            sx={{
              textAlign: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Illustration type="warning" />

            <Text variant="h2" sx={{ textAlign: "center" }}>
              {`We can't find this ${authMethod}`}
            </Text>

            <TryDifferentAuthMethodSection
              authMethod={authMethod}
              supportContext={
                authMethod === DialogAuthMethod.PHONE
                  ? SupportContext.PHONE_LOGIN_WITH_FACILITY_MOBILE_APP_INSTEAD
                  : SupportContext.EMAIL_LOGIN_WITH_FACILITY_MOBILE_APP_INSTEAD
              }
            />

            <WorkplaceAppDownloadCard />
          </Stack>
        </Box>

        <DialogFooter orientation="vertical">
          {authMethod === DialogAuthMethod.PHONE && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                history.push(AuthMode.SIGN_UP);
                modalState.closeModal();
                logEvent(APP_V2_USER_EVENTS.SELECT_SIGN_UP_INSTEAD);
              }}
            >
              Sign Up
            </Button>
          )}

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              modalState.closeModal();
              logEvent(APP_V2_USER_EVENTS.CLOSE_SIGN_UP_INSTEAD_DIALOG);
            }}
          >
            Try a different {authMethod}
          </Button>
        </DialogFooter>
      </FullScreenDialog>
    </ThemeProvider>
  );
}
